import React, { useState, useEffect } from 'react';
import { useResendTimer } from '../hooks/useResendTimer';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { useData } from '../context/DataContext';
import { api, API } from '../lib/api';
import { ShieldCheck, CheckCircle2, ArrowRight, ArrowLeft, KeyRound, Smartphone, Building2, Store, FileCheck, Upload, FileText, X, Gift } from 'lucide-react';

const FALLBACK_CITIES = ['Hyderabad', 'Warangal', 'Nizamabad'];

// Business Types loaded dynamically from /api/business-types (admin-managed via the
// "Business Types" tab in Admin). Kept as a fallback for first paint if the
// fetch is slow / fails so the dropdown is never empty.
const FALLBACK_BUSINESS_TYPES = ['Manufacturer', 'Wholesaler', 'Trader', 'Exporter', 'Service Provider', 'Retailer'];

const STEPS = [
  { id: 1, label: 'Company Details' },
  { id: 2, label: 'Categories & Description' },
  { id: 3, label: 'Verify Mobile' },
  { id: 4, label: 'Done' },
];

const VendorRegister = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { categories, cities: ctxCities = [] } = useData();

  // Already-logged-in vendors land on the dashboard instead of seeing the
  // sign-up form again (they typically arrive via a stale CTA / footer link).
  useEffect(() => {
    try {
      if (localStorage.getItem('tgmart_vendor')) {
        navigate('/vendor/dashboard', { replace: true });
      }
    } catch (_) { /* localStorage unavailable — fall through */ }
  }, []);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [sellerTypes, setSellerTypes] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [districts, setDistricts] = useState([]);
  // GDPR-style explicit T&C consent. Required on the final (OTP) step before
  // we'll let the vendor click "Verify & Submit". Defaults to false so the
  // user has to actively opt-in.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [welcomeCredits, setWelcomeCredits] = useState(0);

  useEffect(() => {
    axios.get(`${API}/payments/config`)
      .then((r) => setWelcomeCredits(Number(r.data?.welcome_credits) || 0))
      .catch(() => { /* banner simply hidden */ });
  }, []);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    mobile: '',
    email: '',
    gst_number: '',
    district: '',
    city: '',
    year_established: '',
    business_type: '',
    seller_type: '',
    categories: [],
    description: '',
    otp: '',
  });
  const [companyDoc, setCompanyDoc] = useState(null);
  const [gstDoc, setGstDoc] = useState(null);
  const [logoImage, setLogoImage] = useState(null);

  // Pull the admin-managed Seller Type catalogue. The public endpoint filters
  // out `verified_only=true` badges so self-signup vendors can't grant
  // themselves a restricted badge.
  useEffect(() => {
    api.getSellerTypes().then(setSellerTypes).catch(() => setSellerTypes([]));
    api.getBusinessTypes().then(setBusinessTypes).catch(() => setBusinessTypes([]));
    api.getDistricts().then(setDistricts).catch(() => setDistricts([]));
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const { remaining: resendIn, start: startResend } = useResendTimer(30);

  // Cascading city list: filter cities by the selected district; fall back to the
  // full list (plus "Other") when no district is chosen or none are linked yet.
  const cityOptions = (() => {
    const all = (ctxCities.length ? ctxCities.map((c) => c.name) : FALLBACK_CITIES).concat(['Other']);
    if (!form.district) return all;
    const filtered = ctxCities.filter((c) => c.district === form.district).map((c) => c.name);
    return filtered.length ? filtered.concat(['Other']) : all;
  })();

  const onPickFile = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Use PDF, JPG, PNG or WEBP.', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 5 MB allowed.', variant: 'destructive' });
      return;
    }
    setter(file);
  };

  const toggleCategory = (id) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(id)
        ? f.categories.filter((x) => x !== id)
        : [...f.categories, id],
    }));
  };

  const next = () => {
    if (step === 1) {
      if (!form.company_name || !form.contact_name || !form.mobile || form.mobile.length < 10 || !form.city) {
        toast({ title: 'Fill all required fields', variant: 'destructive' });
        return;
      }
      if (!companyDoc) {
        toast({ title: 'Upload Company Name proof document', description: 'Incorporation / business registration document is required.', variant: 'destructive' });
        return;
      }
      if (form.gst_number && !gstDoc) {
        toast({ title: 'Upload GST Certificate', description: 'GST document is required when GST number is provided.', variant: 'destructive' });
        return;
      }
    }
    if (step === 2) {
      if (form.categories.length === 0) {
        toast({ title: 'Select at least one category', variant: 'destructive' });
        return;
      }
    }
    setStep(step + 1);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const requestOtp = async () => {
    try {
      setSubmitting(true);
      const res = await api.sendOtp(form.mobile);
      setOtpSent(true);
      startResend();
      if (res.dev_otp) {
        setDevOtp(res.dev_otp);
        toast({ title: 'OTP sent (dev mode)', description: `MSG91 unavailable. Dev OTP: ${res.dev_otp}` });
      } else {
        toast({ title: 'OTP sent', description: `Sent to +91 ${form.mobile}` });
      }
    } catch (err) {
      toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitRegistration = async () => {
    if (!form.otp || form.otp.length < 4) {
      toast({ title: 'Enter the OTP', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('company_name', form.company_name);
      fd.append('contact_name', form.contact_name);
      fd.append('mobile', form.mobile);
      fd.append('otp', form.otp);
      fd.append('city', form.city);
      fd.append('district', form.district || '');
      fd.append('email', form.email || '');
      fd.append('gst_number', form.gst_number || '');
      fd.append('year_established', form.year_established ? String(Number(form.year_established)) : '0');
      fd.append('business_type', form.business_type || '');
      fd.append('seller_type', form.seller_type || '');
      fd.append('categories', (form.categories || []).join(','));
      fd.append('description', form.description || '');
      if (companyDoc) fd.append('company_doc', companyDoc);
      if (gstDoc) fd.append('gst_doc', gstDoc);
      if (logoImage) fd.append('logo_image', logoImage);
      await axios.post(`${API}/vendors/register`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStep(4);
      toast({ title: 'Registration submitted!', description: 'We will review and notify you.' });
    } catch (err) {
      toast({ title: 'Registration failed', description: err?.response?.data?.detail || 'Try again later.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-block px-3 py-1 bg-white/10 backdrop-blur rounded-full text-xs font-medium mb-4 border border-white/20">For Sellers / Manufacturers</div>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-3">Become a Verified Vendor on TelanganaMart</h1>
            <p className="text-blue-100 text-base">Reach lakhs of buyers across Telangana. List your products free of cost and receive direct enquiries.</p>
            {welcomeCredits > 0 && (
              <div
                className="mt-4 inline-flex items-center gap-2.5 bg-gradient-to-r from-[#ee5e23] to-[#f97316] text-white rounded-lg px-4 py-2.5 shadow-lg"
                data-testid="signup-credit-banner"
              >
                <Gift className="w-5 h-5 shrink-0" />
                <div className="text-sm font-semibold leading-snug">
                  Register today &amp; get <span className="underline decoration-2 underline-offset-2">{welcomeCredits} FREE lead credits</span> — unlock buyer contacts instantly!
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { key: 'free', icon: Store, t: 'Free Listing' },
                { key: 'gst', icon: ShieldCheck, t: 'GST Verified' },
                { key: 'leads', icon: FileCheck, t: 'Direct Leads' },
              ].map((it) => (
                <div key={it.key} className="bg-white/10 backdrop-blur border border-white/20 rounded p-3 text-center">
                  <it.icon className="w-5 h-5 mx-auto mb-1" />
                  <div className="text-xs font-medium">{it.t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hidden md:block">
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-lg p-6 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-3 text-white" />
              <div className="text-2xl font-bold">2 Lakh+ Vendors</div>
              <div className="text-sm text-blue-100 mt-1">Already trusting TelanganaMart</div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Stepper — mobile: numbers only with active label below; desktop: full label per step */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    step > s.id ? 'bg-green-600 text-white' :
                    step === s.id ? 'bg-[#ee5e23] text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.id ? <CheckCircle2 className="w-5 h-5" /> : s.id}
                  </div>
                  {/* Per-step labels only render from sm: up — on phones a single
                      label is shown below the row to avoid the overlap shown in
                      mobile screenshots. */}
                  <div className={`hidden sm:block text-xs mt-1 text-center px-1 whitespace-nowrap ${step === s.id ? 'text-[#ee5e23] font-semibold' : 'text-gray-600'}`}>
                    {s.label}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 sm:mx-1 ${step > s.id ? 'bg-green-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          {/* Mobile-only active-step label */}
          <div className="sm:hidden mt-2 text-center">
            <span className="text-sm font-semibold text-[#ee5e23]">
              Step {step} of {STEPS.length} · {STEPS.find((s) => s.id === step)?.label}
            </span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Company Details</h2>
              <p className="text-sm text-gray-600 mb-5">Tell us about your business.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-1 sm:col-span-2">
                  <Label className="text-xs">Company Name *</Label>
                  <Input value={form.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder="e.g., Shree Steel Industries" />
                </div>
                <div>
                  <Label className="text-xs">Contact Person *</Label>
                  <Input value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} placeholder="Full name" />
                </div>
                <div>
                  <Label className="text-xs">Mobile *</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="flex items-center px-3 border rounded-md bg-gray-50 text-sm text-gray-700">+91</div>
                    <Input value={form.mobile} onChange={(e) => update('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="contact@company.com" />
                </div>
                <div>
                  <Label className="text-xs">GST Number</Label>
                  <Input value={form.gst_number} onChange={(e) => update('gst_number', e.target.value.toUpperCase())} placeholder="36AAAAA0000A1Z5" maxLength={15} />
                </div>
                <div>
                  <Label className="text-xs">District</Label>
                  <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v, city: '' })}>
                    <SelectTrigger data-testid="vendor-district-trigger"><SelectValue placeholder="Select district" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {districts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">City *</Label>
                  <Select value={form.city} onValueChange={(v) => update('city', v)}>
                    <SelectTrigger data-testid="vendor-city-trigger"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Year Established</Label>
                  <Input type="number" value={form.year_established} onChange={(e) => update('year_established', e.target.value)} placeholder="e.g., 2015" />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <Label className="text-xs">Business Type</Label>
                  <Select value={form.business_type} onValueChange={(v) => update('business_type', v)}>
                    <SelectTrigger data-testid="vendor-business-type-trigger"><SelectValue placeholder="Select business type" /></SelectTrigger>
                    <SelectContent>
                      {(businessTypes.length ? businessTypes.map((b) => b.name) : FALLBACK_BUSINESS_TYPES).map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {sellerTypes.length > 0 && (
                  <div className="col-span-1 sm:col-span-2" data-testid="vendor-seller-type-wrap">
                    <Label className="text-xs">Seller Type</Label>
                    <Select value={form.seller_type} onValueChange={(v) => update('seller_type', v)}>
                      <SelectTrigger data-testid="vendor-seller-type-trigger"><SelectValue placeholder="Select seller type" /></SelectTrigger>
                      <SelectContent>
                        {sellerTypes.map((st) => (
                          <SelectItem key={st.id} value={st.name} data-testid={`vendor-seller-type-option-${st.slug}`}>{st.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-gray-500 mt-1">Restricted badges (e.g. &quot;Verified Exporter&quot;) are assigned by admin after verification.</p>
                  </div>
                )}

                <div className="col-span-1 sm:col-span-2 border-t border-gray-200 pt-4 mt-2">
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Verification Documents</h3>
                  <p className="text-xs text-gray-500 mb-3">PDF / JPG / PNG / WEBP, max 5 MB each.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <FileUploadField
                      label="Company Logo (optional)"
                      hint="Square JPG/PNG preferred"
                      file={logoImage}
                      onChange={onPickFile(setLogoImage)}
                      onClear={() => setLogoImage(null)}
                      accept=".jpg,.jpeg,.png,.webp,image/*"
                    />
                    <FileUploadField
                      label="Company Name Proof *"
                      hint="Incorporation / business reg."
                      file={companyDoc}
                      onChange={onPickFile(setCompanyDoc)}
                      onClear={() => setCompanyDoc(null)}
                    />
                    <FileUploadField
                      label={form.gst_number ? 'GST Certificate *' : 'GST Certificate (optional)'}
                      hint="Required if GST number provided"
                      file={gstDoc}
                      onChange={onPickFile(setGstDoc)}
                      onClear={() => setGstDoc(null)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Categories & Description</h2>
              <p className="text-sm text-gray-600 mb-5">Choose categories you sell in. You can update later.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5 max-h-64 overflow-y-auto p-1">
                {categories.map((c) => (
                  <label key={c.id} className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-sm transition-colors ${
                    form.categories.includes(c.id) ? 'border-[#ee5e23] bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <Checkbox checked={form.categories.includes(c.id)} onCheckedChange={() => toggleCategory(c.id)} />
                    <span className="truncate">{c.name}</span>
                  </label>
                ))}
              </div>
              <div>
                <Label className="text-xs">About your business</Label>
                <Textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={4}
                  placeholder="Briefly describe your products, manufacturing, certifications, capacity..." />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify your Mobile</h2>
              <p className="text-sm text-gray-600 mb-5">We&apos;ll send a 4-digit OTP to +91 {form.mobile}.</p>
              {!otpSent && (
                <Button onClick={requestOtp} disabled={submitting} className="bg-[#2c54b2] hover:bg-[#1f3f87] text-white">
                  <Smartphone className="w-4 h-4 mr-2" /> {submitting ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              )}
              {otpSent && (
                <div className="mt-4 space-y-3 max-w-xs">
                  <div>
                    <Label className="text-xs">Enter OTP</Label>
                    <Input value={form.otp} onChange={(e) => update('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center tracking-widest text-lg" placeholder="4-digit OTP" data-testid="vendor-otp-input" />
                  </div>
                  {devOtp && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      Dev mode: OTP is <b className="font-mono">{devOtp}</b>
                    </div>
                  )}
                  <button onClick={requestOtp} disabled={submitting || resendIn > 0} data-testid="vendor-resend-otp" className="text-xs text-[#2c54b2] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed">
                    {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                  </button>
                  <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer select-none pt-2" data-testid="vendor-register-terms-wrap">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-0.5 accent-[#ee5e23]"
                      data-testid="vendor-register-terms"
                    />
                    <span>
                      I have read and agree to the{' '}
                      <a href="/p/terms-and-conditions" target="_blank" rel="noreferrer" className="text-[#2c54b2] underline">Terms &amp; Conditions</a>
                      ,{' '}
                      <a href="/p/vendor-terms" target="_blank" rel="noreferrer" className="text-[#2c54b2] underline">Vendor Agreement</a>
                      {' '}and{' '}
                      <a href="/p/privacy-policy" target="_blank" rel="noreferrer" className="text-[#2c54b2] underline">Privacy Policy</a>
                      .
                    </span>
                  </label>
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Submitted!</h2>
              <p className="text-sm text-gray-600 mb-1">Thank you for registering with TelanganaMart.</p>
              <p className="text-sm text-gray-600 mb-6">
                Our team will verify your details and activate your vendor profile within 24-48 hours.
                You&apos;ll receive a confirmation on <b>+91 {form.mobile}</b>.
              </p>              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/')} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white">Back to Home</Button>
                <Button onClick={() => { setStep(1); setForm({ company_name: '', contact_name: '', mobile: '', email: '', gst_number: '', district: '', city: '', year_established: '', business_type: '', seller_type: '', categories: [], description: '', otp: '' }); setDevOtp(null); setOtpSent(false); }} variant="outline">Register Another</Button>
              </div>
            </div>
          )}

          {step < 4 && (
            <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={back} disabled={step === 1}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < 3 && (
                <Button onClick={next} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 3 && (
                <Button onClick={submitRegistration} disabled={submitting || !form.otp || !acceptedTerms} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="vendor-register-submit">
                  <KeyRound className="w-4 h-4 mr-1" /> {submitting ? 'Submitting...' : 'Verify & Submit'}
                </Button>
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Already registered? <Link to="/" className="text-[#2c54b2] hover:underline">Sign in</Link> to your vendor dashboard.
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default VendorRegister;

const FileUploadField = ({ label, hint, file, onChange, onClear, accept = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*' }) => {
  const inputId = `upload-${label.replace(/\s+/g, '-')}`;
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {!file ? (
        <label htmlFor={inputId} className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-[#ee5e23] rounded-md py-4 px-2 cursor-pointer transition-colors bg-gray-50 hover:bg-orange-50">
          <Upload className="w-5 h-5 text-gray-500 mb-1" />
          <span className="text-xs text-gray-700 font-medium">Click to upload</span>
          <span className="text-[10px] text-gray-500 mt-0.5 text-center px-1">{hint}</span>
          <input
            id={inputId}
            type="file"
            accept={accept}
            className="hidden"
            onChange={onChange}
          />
        </label>
      ) : (
        <div className="mt-1 flex items-center gap-2 border border-green-300 bg-green-50 rounded-md px-3 py-2.5">
          <FileText className="w-4 h-4 text-green-700 shrink-0" />
          <div className="text-xs text-green-800 min-w-0 flex-1">
            <div className="font-medium truncate">{file.name}</div>
            <div className="text-[10px] text-green-700">{(file.size / 1024).toFixed(0)} KB • uploaded</div>
          </div>
          <button type="button" onClick={onClear} className="text-gray-500 hover:text-red-600 p-1 rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
